import type { KdsBoardConfig } from '@shared/kds/table-board';
import i18n from '../i18n';
import { FEED_TIMEOUT_MS, FULL_LIST_TIMEOUT_MS } from '../lib/sync/request-signal';
import { request, requestEnvelope } from './http';

export { publicRequest } from './http';

const AUTH_URL = '/api/auth';

// --- Auth (uses /api/auth, not /api/kitchen) ---
export const authApi = {
  login: async (email: string, password: string) => {
    const res = await fetch(`${AUTH_URL}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error || i18n.t('kds.login.failed', 'Login fallito'));
    }
    return json.data as {
      token: string;
      user: { id: string; email: string; name: string; role: string };
    };
  },

  getMyClients: async (token: string) => {
    const res = await fetch(`${AUTH_URL}/me/clients`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      throw new Error(json.error || i18n.t('kds.login.restaurantsLoadFailed', 'Errore nel recupero dei ristoranti'));
    }
    return json.data as { id: string; name: string }[];
  },
};

// --- Stations ---
export const stationsApi = {
  list: (clientId: string) => request<any[]>(`/stations?clientId=${clientId}`),
  get: (id: string) => request<any>(`/stations/${id}`),
};

// --- Orders ---
/** Orders the kitchen board asks for in one go (research D11: ORDER_SYNC_LIMIT). */
export const ORDER_LIST_LIMIT = 500;

function orderListQuery(clientId: string, params?: { status?: string; date?: string }): string {
  // Filter out undefined values before building the query string.
  // URLSearchParams converts undefined to the string "undefined", which
  // causes the backend to filter for a literal status of "undefined".
  const defined: Record<string, string> = { clientId, limit: String(ORDER_LIST_LIMIT) };
  if (params?.status !== undefined) defined.status = params.status;
  if (params?.date !== undefined) defined.date = params.date;
  return new URLSearchParams(defined).toString();
}

export interface ChangesResponse {
  resync: boolean;
  cursor: string;
  orders?: any[];
  removedOrderIds?: string[];
}

export const ordersApi = {
  list: (clientId: string, params?: { status?: string; date?: string }) =>
    request<any[]>(`/orders?${orderListQuery(clientId, params)}`),
  /** The list plus the feed cursor read with it (feature 334). */
  listWithCursor: async (
    clientId: string,
    params?: { status?: string; date?: string },
    signal?: AbortSignal,
  ): Promise<{ orders: any[]; cursor: string | undefined }> => {
    const json = await requestEnvelope<any[]>(`/orders?${orderListQuery(clientId, params)}`, {
      signal,
      timeoutMs: FULL_LIST_TIMEOUT_MS,
    });
    return { orders: json.data ?? [], cursor: json.cursor };
  },
  /** Orders changed since `since`. 404 means the feed is off for this venue or the backend is old. */
  changes: async (clientId: string, since: string, signal?: AbortSignal): Promise<ChangesResponse> => {
    const qs = new URLSearchParams({ clientId, since }).toString();
    const json = await requestEnvelope<unknown>(`/orders/changes?${qs}`, {
      signal,
      timeoutMs: FEED_TIMEOUT_MS,
    });
    return {
      resync: json.resync === true,
      cursor: String(json.cursor ?? since),
      orders: json.orders as any[] | undefined,
      removedOrderIds: json.removedOrderIds as string[] | undefined,
    };
  },
  get: (id: string) => request<any>(`/orders/${id}`),
  create: (data: any) => request<any>('/orders', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: any) =>
    request<any>(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  fireCourse: (id: string) => request<any>(`/orders/${id}/fire-course`, { method: 'POST' }),
  complete: (id: string) => request<any>(`/orders/${id}/complete`, { method: 'POST' }),
};

// --- Items ---
export const itemsApi = {
  updateStatus: (id: string, status: string) =>
    request<any>(`/items/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
  bump: (id: string) => request<any>(`/items/${id}/bump`, { method: 'POST' }),
};

// --- Station view ---
export const stationViewApi = {
  get: (stationId: string, clientId: string) =>
    request<any[]>(`/station-view/${stationId}?clientId=${clientId}`),
};

// --- Public (no auth) ---
export const publicApi = {
  getMenu: (token: string) => request<any>(`/public/menu/${token}`),
  placeOrder: (token: string, data: { items: any[]; customerName?: string; notes?: string }) =>
    request<any>(`/public/order/${token}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// --- Public Payment (no auth) ---
export const publicPaymentApi = {
  createCheckout: (token: string, orderId: string) =>
    request<{ sessionId: string; url: string }>(`/public/checkout/${token}`, {
      method: 'POST',
      body: JSON.stringify({ orderId }),
    }),
  checkPaymentStatus: (sessionId: string) =>
    request<{
      status: string;
      orderId: string;
      orderNumber?: number;
      estimatedMinutes?: number;
    }>(`/public/payment-status/${sessionId}`),
};

// --- Feature 084: Client Config (includes kdsBoardConfig) ---

export interface ClientConfig {
  secondaryCashEnabled: boolean;
  secondaryCashVisible: boolean;
  maxPlafondDiscountPercent: number;
  sociDiscountEnabled: boolean;
  tipsModuleEnabled: boolean;
  kdsBoardConfig: KdsBoardConfig;
  // Feature 113 — blocco ordini cibo
  foodOrderBlockEnabled?: boolean;
  foodOrderBlockActive?: boolean;
  // Feature 148 — cutoff giornata lavorativa (ora Rome, es. 4 = le 04:00).
  // Non ancora esposto dal backend: se assente, lo Storico usa il default 4.
  businessDayCutoffHour?: number;
  /**
   * Feature 252 — the venue wants identical dishes on one line with their
   * count. Presentation only: the portions underneath are untouched. OFF is
   * also what a surface that cannot read the flag looks like, and OFF means
   * today's behaviour, one line per portion.
   */
  groupIdenticalDishes: boolean;
  /**
   * Feature 268 — the venue shows the diner tag on the surfaces used while
   * serving. Optional on the wire so a backend that has not shipped it yet
   * reads as OFF, which is today's behaviour: nothing shown at all.
   */
  dinerTagOnServiceEnabled?: boolean;
}

export const clientConfigApi = {
  get: (clientId: string, signal?: AbortSignal): Promise<ClientConfig> =>
    request<ClientConfig>(`/client-config?clientId=${encodeURIComponent(clientId)}`, {
      signal,
      timeoutMs: FEED_TIMEOUT_MS,
    }),
};

// --- Feature 113: Blocco ordini cibo (toggle chef da KDS) ---

export const foodBlockApi = {
  toggle: (
    clientId: string,
    active: boolean,
  ): Promise<{ clientId: string; enabled: boolean; active: boolean; changedAt: string }> =>
    request<{ clientId: string; enabled: boolean; active: boolean; changedAt: string }>(
      '/food-block/toggle',
      {
        method: 'POST',
        body: JSON.stringify({ clientId, active }),
      },
    ),
};

// --- Feature 022: KDS Voice Personalities ---
export interface VoiceAnnounceResponse {
  success?: boolean;
  silent?: boolean;
  audioUrl?: string;
  fallbackText?: string;
  cached?: boolean;
  generatedText?: string;
  source?: string;
}

export const kdsVoiceApi = {
  /**
   * Notifica al backend un evento KDS e riceve audio URL o fallbackText.
   * Mai throw — error path → silent (FR-017).
   */
  announce: (
    eventType: 'new_order' | 'course_fired' | 'item_ready' | 'nag_overdue',
    payload: Record<string, unknown>,
  ) =>
    request<VoiceAnnounceResponse>(`/tts/announce`, {
      method: 'POST',
      body: JSON.stringify({ eventType, payload }),
    }).catch(() => ({ success: false, silent: true }) as VoiceAnnounceResponse),
};
